import * as yup from 'yup';
import { VERSICHERUNGSART } from '@/features/Sparten/shared/types';
import { isBeamter } from '@/features/Sparten/pkv/formConfigs/sharedRenderConditions';
import { locales } from '@/features/validation/locales';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { allgemeinSchema } from '@/features/Sparten/pkv/schema/allgemeinSchema/allgemeinSchema';
import { stationaerOptionenSchema } from '@/features/Sparten/pkv/schema/stationaerOptionenSchema/stationaerOptionenSchema';
import { ambulantOptionenSchema } from '@/features/Sparten/pkv/schema/ambulantOptionenSchema/ambulantOptionenSchema';
import { zahnOptionenSchema } from '@/features/Sparten/pkv/schema/zahnOptionenSchema/zahnOptionenSchema';
import { krankentagegeldOptionenSchema } from '@/features/Sparten/pkv/schema/krankentagegeldOptionenSchema/krankentagegeldOptionenSchema';
import { krankenhaustagegeldOptionenSchema } from '@/features/Sparten/pkv/schema/krankenhaustagegeldOptionenSchema/krankenhaustagegeldOptionenSchema';
import { auslandsreisekrankenversicherungOptionenSchema } from '@/features/Sparten/pkv/schema/auslandsreisekrankenversicherungOptionenSchema/auslandsreisekrankenversicherungOptionenSchema';
import { pflegetagegeldOptionenSchema } from '@/features/Sparten/pkv/schema/pflegetagegeldOptionenSchema/pflegetagegeldOptionenSchema';
import { pflegepflichtversicherungOptionenSchema } from '@/features/Sparten/pkv/schema/pflegepflichtversicherungOptionenSchema/pflegepflichtversicherungOptionenSchema';

export const test5erSteps = (value: number | null): boolean =>
  value !== null ? value % 5 === 0 : true;

export const validateProductsZusatz = (
  testContext: yup.TestContext,
): boolean => {
  const {
    versicherungsart,
    ambulant,
    stationaer,
    zahn,
    krankentagegeld,
    krankenhaustagegeld,
    pflegezusatz,
  } = testContext.options.context as {
    versicherungsart: VERSICHERUNGSART;
    ambulant: boolean;
    stationaer: boolean;
    zahn: boolean;
    krankentagegeld: boolean;
    krankenhaustagegeld: boolean;
    pflegezusatz: boolean;
  };

  if (versicherungsart === VERSICHERUNGSART.ZUSATZ) {
    return (
      ambulant ||
      stationaer ||
      zahn ||
      krankentagegeld ||
      krankenhaustagegeld ||
      pflegezusatz
    );
  }

  return true;
};

export const pkvSchema = yup.object({
  berufsstatus: yup.string().default(''),
  berufsgruppe: yup.string().default(''),
  versicherungsart: yup
    .string()
    .default(VERSICHERUNGSART.VOLL)
    .oneOf([VERSICHERUNGSART.VOLL, VERSICHERUNGSART.ZUSATZ]),
  leistungstraeger: yup
    .string()
    .default('')
    .when({
      is: () => isBeamter(),
      then: yup
        .string()
        .test(
          'isRequired',
          locales.mixed?.required as string,
          (val) =>
            !getSimpleChoiceMeta(
              SIMPLE_CHOICE.LEISTUNGSTRAEGER,
              SIMPLE_CHOICE_META.INVALID,
            ).includes(val ?? ''),
        ),
    }),
  beihilfesaetze: yup
    .string()
    .default('')
    .when({
      is: () => isBeamter(),
      then: yup
        .string()
        .test(
          'isRequired',
          locales.mixed?.required as string,
          (val) =>
            !getSimpleChoiceMeta(
              SIMPLE_CHOICE.BEIHILFESAETZE,
              SIMPLE_CHOICE_META.INVALID,
            ).includes(val ?? ''),
        ),
    }),

  allgemein: allgemeinSchema,
  stationaer: yup.boolean().default(true),
  stationaerOptionen: stationaerOptionenSchema,
  ambulant: yup
    .boolean()
    .default(true)
    .test({
      name: 'isRequired',
      message: 'Mindestens ein Baustein muss ausgewählt sein',
      test() {
        return validateProductsZusatz(this);
      },
    }),
  ambulantOptionen: ambulantOptionenSchema,
  zahn: yup.boolean().default(true),
  zahnOptionen: zahnOptionenSchema,
  krankentagegeld: yup.boolean().default(false),
  krankentagegeldOptionen: krankentagegeldOptionenSchema,
  krankenhaustagegeld: yup.boolean().default(false),
  krankenhaustagegeldOptionen: krankenhaustagegeldOptionenSchema,
  auslandsreisekrankenversicherung: yup.boolean().default(false),
  auslandsreisekrankenversicherungOptionen:
    auslandsreisekrankenversicherungOptionenSchema,
  pflegepflichtversicherung: yup.boolean().default(true),
  pflegepflichtversicherungOptionen: pflegepflichtversicherungOptionenSchema,
  pflegezusatz: yup.boolean().default(false),
  pflegezusatzOptionen: pflegetagegeldOptionenSchema,
  temporaryInputFields: yup.object({
    isKieferorthopaedieMinDirty: yup.boolean().default(false),
    isMaterialLaborKostenDirty: yup.boolean().default(false),
  }),
});
